import React from "react";
import { useRef, useState, useEffect } from "react";
import {
  setDocData,
  addNewDoc,
  storage,
  getAllBuildingPhotosSnap,
  deleteDocById,
  deleteObjectByUrl,
  getDocDataSnap,
} from "../Firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useSelector } from "react-redux";
import { v4 } from "uuid";
import PhotoViewModal from "../components/PhotoViewModal";
import { toast } from "react-toastify";
import {
  FiUpload,
  FiEdit,
  FiSave,
  FiPlus,
  FiX,
  FiImage,
  FiStar,
} from "react-icons/fi";

function BuildingPhotos({ buildingId, buildingDoc }) {
  const inputref = useRef();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedImg, setSelectedImg] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [primaryPhoto, setPrimaryPhoto] = useState(null);

  const currentUser = useSelector((state) => state.userObject);

  useEffect(() => {
    const getPhotos = async () => {
      const res = getAllBuildingPhotosSnap(
        "buildingPhotos",
        buildingId,
        (data) => {
          // arrange photos by date
          data.sort((a, b) => b.createdAt - a.createdAt);
          setPhotos(data);
        }
      );
    };
    getPhotos();
  }, [file]);

  useEffect(() => {
    const primary = buildingDoc.primaryPhoto;
    setPrimaryPhoto(primary);
  }, [buildingDoc]);

  const handleClick = (e) => {
    inputref.current.click();
  };

  const handleSelect = (e) => {
    const selected = e.target.files[0];
    setFile(selected);

    if (selected) {
      const handleUpload = async () => {
        const fileName = `buildings/${buildingId}/photos/${
          selected.name
        }+${v4()}`;
        const storageRef = ref(storage, fileName);
        const uploadTask = uploadBytesResumable(storageRef, selected);

        const res = uploadTask.on(
          "state_changed",
          (snap) => {
            let percentage = (snap.bytesTransferred / snap.totalBytes) * 100;
            setProgress(percentage);
          },
          (err) => {
            setError(err);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const url = downloadURL;
            const createdAt = new Date();
            const res = await addNewDoc("buildingPhotos", {
              buildingId,
              url,
              createdAt,
              createdBy: currentUser.email,
            });

            setProgress(0);
          }
        );
        setProgress(0);
      };
      handleUpload();
    } else {
      setFile(null);
      setProgress(0);
    }
  };

  const handleDelelete = (item) => {
    if (window.confirm("Are you sure you want to delete this photo?")) {
      deleteDocById("buildingPhotos", item.id);
      deleteObjectByUrl(item.url);
      toast.success("Photo deleted");
    } else {
      return;
    }
  };

  const handlePrimarySet = (photo) => {
    const res = setDocData("masterlist", buildingId, {
      primaryPhoto: photo.id,
      primaryPhotoUrl: photo.url,
    });
    toast.success("Primary photo set");
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-lg font-semibold text-[#002368] flex items-center gap-2">
          <FiImage size={20} />
          <span>Building Photos</span>
        </div>

        <div className="flex items-center gap-3">
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
              editMode
                ? "bg-[#ff5003] text-white hover:bg-opacity-90"
                : "bg-[#002368] text-white hover:bg-opacity-90"
            }`}
            onClick={() => {
              setEditMode(!editMode);
            }}
          >
            {editMode ? (
              <>
                <FiSave size={16} />
                <span>Save</span>
              </>
            ) : (
              <>
                <FiEdit size={16} />
                <span>Edit</span>
              </>
            )}
          </button>

          <button
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-all"
            onClick={handleClick}
          >
            <FiUpload size={16} />
            <span>Upload</span>
          </button>

          <input
            ref={inputref}
            className="hidden"
            type="file"
            accept="image/png, image/jpeg"
            onChange={handleSelect}
          />
        </div>
      </div>

      {progress > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
          <div
            className="bg-[#ff5003] h-2.5 rounded-full"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      {photos.length === 0 && (
        <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <FiImage size={48} className="text-gray-400 mb-3" />
          <p className="text-gray-500">No photos uploaded yet</p>
          <button
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#002368] text-white rounded-md hover:bg-opacity-90 transition-all"
            onClick={handleClick}
          >
            <FiPlus size={16} />
            <span>Add Photos</span>
          </button>
        </div>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo, index) => (
            <div
              key={index}
              className="relative group rounded-lg overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all"
            >
              <img
                src={photo.url}
                className="w-full h-48 object-cover cursor-pointer"
                alt="Building photo"
                onClick={() => setSelectedImg(photo.url)}
              />

              {editMode && (
                <>
                  <button
                    className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                    onClick={() => handleDelelete(photo)}
                    title="Delete photo"
                  >
                    <FiX size={16} className="text-red-500" />
                  </button>

                  <button
                    className={`absolute bottom-0 left-0 right-0 py-2 flex items-center justify-center gap-1 text-sm font-medium transition-all ${
                      photo.id === primaryPhoto
                        ? "bg-[#002368] text-white"
                        : "bg-black/50 text-white hover:bg-[#002368]/70"
                    }`}
                    onClick={() => handlePrimarySet(photo)}
                  >
                    <FiStar
                      size={14}
                      className={
                        photo.id === primaryPhoto
                          ? "text-[#ff5003]"
                          : "text-white"
                      }
                    />
                    <span>
                      {photo.id === primaryPhoto
                        ? "Primary Photo"
                        : "Set as Primary"}
                    </span>
                  </button>
                </>
              )}

              {!editMode && photo.id === primaryPhoto && (
                <div className="absolute top-2 left-2 bg-[#002368] text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <FiStar size={12} className="text-[#ff5003]" />
                  <span>Primary</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedImg && (
        <PhotoViewModal
          selectedImg={selectedImg}
          setSelectedImg={setSelectedImg}
        />
      )}
    </div>
  );
}

export default BuildingPhotos;
